// The dashboard: today at a glance (review CR8B-9). Every tile comes from an endpoint the
// Admin already uses and links to the screen that deals with it:
//   - approvals waiting for a decision (approvals inbox);
//   - station actions held for a manager (Settings → Held actions, devices.manage);
//   - today's batches with flags (Production);
//   - deliveries due in: sent orders expected today or already late (Purchasing);
//   - stations whose devices haven't synced lately (Settings → Stations, devices.manage).
// A tile shows only to someone who may open its screen (D-070). Each says honestly what it
// knows: a skeleton while it loads, "Couldn't load" (never 0) when the request failed. "Today"
// is the business's trading day (D-062, accountToday), not the browser's date. The import
// prompt shows only while the catalogue is still empty. A figure counts every page of its list
// (UI8D-4): one page of 50 could hide a flagged batch behind a firm 0.

import { brand } from '@enp/brand';
import { canOpenArea } from '@enp/domain/permissions';
import { Button, EmptyState, PageHeader, StatTile } from '@enp/ui';
import { useInfiniteQuery, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowRight,
  ChefHat,
  ClipboardCheck,
  FileInput,
  MonitorSmartphone,
  ShieldAlert,
  Truck,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { meQueryOptions } from '../../auth/session.js';
import { productListQuery } from '../../catalogue/queries.js';
import { batchesQuery } from '../../production/queries.js';
import { ordersQuery } from '../../purchasing/queries.js';
import { stationHealth } from '../../dashboard/health.js';
import { useDocumentTitle } from '../../shell/page.js';
import { holdsQuery, stationsQuery } from '../../stations/queries.js';
import { waitingApprovalsQuery, waitingText } from '../../stock-control/queries.js';
import { accountToday } from '../../stock/labels.js';

export const Route = createFileRoute('/_app/')({
  component: Dashboard,
});

/** Morning, afternoon or evening at the business (the account's zone), not the browser. */
function greeting(timeZone: string, now = new Date()): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-ZA', { hour: 'numeric', hourCycle: 'h23', timeZone }).format(now),
  );
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** Pages a tile reads at most (500 rows): beyond that it says "+", never a smaller number. */
const MAX_PAGES = 10;

/**
 * Every page of an infinite list, up to MAX_PAGES: loading until they are all in, so a tile
 * never shows a figure from the first page alone (UI8D-4).
 */
function useAllPages<T>(q: {
  data?: { pages: { items: T[]; next_cursor: string | null }[] };
  isPending: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}): { items: T[]; loading: boolean; more: boolean } {
  const pages = q.data?.pages ?? [];
  const wantMore = q.hasNextPage && pages.length < MAX_PAGES && !q.isError;
  const { fetchNextPage, isFetchingNextPage } = q;
  useEffect(() => {
    if (wantMore && !isFetchingNextPage) void fetchNextPage();
  }, [wantMore, isFetchingNextPage, fetchNextPage]);
  return {
    items: pages.flatMap((p) => p.items),
    loading: q.isPending || wantMore,
    more: q.hasNextPage && !wantMore,
  };
}

/** What a tile shows for its request: loading, failed, or the figure. */
interface TileState {
  loading: boolean;
  error: boolean;
  value?: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'warning' | 'danger' | 'success';
}

function GlanceTile({
  label,
  to,
  search,
  icon,
  state,
  testId,
}: {
  label: string;
  to: string;
  search?: Record<string, string>;
  icon: LucideIcon;
  state: TileState;
  testId: string;
}) {
  return (
    <StatTile
      data-testid={testId}
      className="transition-colors hover:bg-surface-hover"
      icon={icon}
      label={
        <Link
          to={to}
          {...(search ? { search } : {})}
          className="after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
        >
          {label}
        </Link>
      }
      loading={state.loading}
      value={state.error ? null : (state.value ?? null)}
      hint={state.error ? 'Couldn’t load this. Reload the page to try again.' : state.hint}
      tone={state.error ? 'neutral' : (state.tone ?? 'neutral')}
    />
  );
}

function Dashboard() {
  useDocumentTitle('Dashboard');
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const can = {
    approvals: canOpenArea(me.permissions, 'approvals'),
    production: canOpenArea(me.permissions, 'production'),
    purchasing: canOpenArea(me.permissions, 'purchasing'),
    devices: me.permissions.includes('devices.manage'),
    imports: canOpenArea(me.permissions, 'imports'),
  };
  const anyTile = can.approvals || can.production || can.purchasing || can.devices;

  return (
    <>
      <PageHeader
        title={greeting(me.account.timezone)}
        description={`Here’s what needs attention at ${me.account.name} today.`}
      />
      <CatalogueStart canImport={can.imports} />
      {anyTile ? (
        <section
          aria-label="Today at a glance"
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
        >
          {can.approvals && <ApprovalsTile />}
          {can.devices && <HeldActionsTile />}
          {can.production && <FlaggedBatchesTile today={today} />}
          {can.purchasing && <DeliveriesTile today={today} />}
          {can.devices && <StationsTile />}
        </section>
      ) : (
        <EmptyState
          compact
          icon={ClipboardCheck}
          title="Nothing to watch here for your role"
          description="The screens in the menu are the ones you can use. Ask an owner if you need another."
        />
      )}
    </>
  );
}

/** The import prompt, only while the catalogue is still empty (CR8B-9). */
function CatalogueStart({ canImport }: { canImport: boolean }) {
  const products = useInfiniteQuery(productListQuery({}));
  const first = products.data?.pages[0];
  const empty = first !== undefined && first.items.length === 0 && first.next_cursor === null;
  if (!empty) return null;
  return (
    <EmptyState
      compact
      className="mb-8"
      icon={FileInput}
      title="Start by bringing in your catalogue"
      description={`Import your items and recipes from your till system. Stock, expiry, production and purchasing build on the catalogue, and ${brand.productName} fills this page in as you use them.`}
      action={
        // Only to someone who can open Imports (D-070, B-061).
        canImport ? (
          <Button asChild>
            <Link to="/imports">
              Go to Imports
              <ArrowRight aria-hidden />
            </Link>
          </Button>
        ) : undefined
      }
    />
  );
}

function ApprovalsTile() {
  const q = useQuery(waitingApprovalsQuery());
  const n = q.data?.count ?? 0;
  return (
    <GlanceTile
      testId="tile-approvals"
      label="Waiting for approval"
      to="/approvals"
      icon={ClipboardCheck}
      state={{
        loading: q.isPending,
        error: q.isError,
        value: q.data ? waitingText(q.data) : undefined,
        hint: n === 0 ? 'Nothing waiting for a decision' : 'Counts, write-offs and requests',
        tone: n > 0 ? 'warning' : 'neutral',
      }}
    />
  );
}

function HeldActionsTile() {
  const q = useQuery(holdsQuery('open'));
  const n = q.data?.open_count ?? 0;
  return (
    <GlanceTile
      testId="tile-held"
      label="Held for a manager"
      to="/settings/held-actions"
      icon={ShieldAlert}
      state={{
        loading: q.isPending,
        error: q.isError,
        value: q.data ? n : undefined,
        hint:
          n === 0
            ? 'Every station action reached the books'
            : `Station ${plural(n, 'action', 'actions')} the office couldn’t book`,
        tone: n > 0 ? 'warning' : 'neutral',
      }}
    />
  );
}

function FlaggedBatchesTile({ today }: { today: string }) {
  const q = useInfiniteQuery(batchesQuery({ day: today }));
  const { items: batches, loading, more } = useAllPages(q);
  const flagged = batches.filter((b) => b.flag_count > 0).length;
  return (
    <GlanceTile
      testId="tile-batches"
      label="Batches with flags today"
      to="/production"
      search={{ day: today }}
      icon={ChefHat}
      state={{
        loading,
        error: q.isError,
        value: loading ? undefined : `${flagged}${more ? '+' : ''}`,
        hint:
          batches.length === 0
            ? 'No batches today'
            : `Of ${batches.length}${more ? '+' : ''} ${plural(batches.length, 'batch', 'batches')} today`,
        tone: flagged > 0 ? 'warning' : 'neutral',
      }}
    />
  );
}

function DeliveriesTile({ today }: { today: string }) {
  // Open orders due by today: those sent can arrive (late ones too, UI8D-3); one not sent
  // yet can't, so it isn't counted.
  const q = useInfiniteQuery(ordersQuery({ view: 'expected', to: today }));
  const { items, loading, more } = useAllPages(q);
  const due = items.filter((o) => o.status !== 'open');
  const n = due.length;
  const late = due.filter((o) => o.expected_date !== null && o.expected_date < today).length;
  return (
    <GlanceTile
      testId="tile-deliveries"
      label="Deliveries expected today"
      to="/purchasing"
      search={{ view: 'expected', to: today }}
      icon={Truck}
      state={{
        loading,
        error: q.isError,
        value: loading ? undefined : `${n}${more ? '+' : ''}`,
        hint:
          n === 0
            ? 'No orders due in today'
            : late > 0
              ? `Sent orders due today, ${late} late`
              : 'Sent orders due today',
        tone: late > 0 ? 'warning' : 'neutral',
      }}
    />
  );
}

function StationsTile() {
  const q = useQuery(stationsQuery());
  // Measured from when the list arrived (pure: no clock read while rendering).
  const health = q.data ? stationHealth(q.data.stations, q.dataUpdatedAt) : undefined;
  return (
    <GlanceTile
      testId="tile-stations"
      label="Stations syncing"
      to="/settings/stations"
      icon={MonitorSmartphone}
      state={{
        loading: q.isPending,
        error: q.isError,
        value: health ? `${health.active - health.quiet} of ${health.active}` : undefined,
        hint: !health
          ? undefined
          : health.active === 0
            ? 'No station paired yet'
            : health.quiet > 0
              ? `${health.quiet} not heard from in the last 30 minutes`
              : 'All heard from in the last 30 minutes',
        tone: health && health.quiet > 0 ? 'warning' : 'neutral',
      }}
    />
  );
}
